import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import type { BeforeCapture, DropResult } from "@hello-pangea/dnd";
import type { PropertyMappings } from "../../../../shared/PropertyMappings.types";
import {
	PropertyOption,
	PropertySortOption,
	GlobalPropertyMappingSelectorProps,
	GlobalPropertyMappingSelectorViewModel,
} from "./GlobalPropertyMappingSelector.types";
import {
	FIELD_CONFIGS,
	PRIMARY_PROPERTY_FIELDS_ORDERED,
} from "../PropertyMappingSelector/PropertyMappingSelector.constants";
import { DefaultImportTableColumns, ImportTableColumn } from "../../../../../../utilities/types";
import {
	getUnassignedProperties,
	filterProperties,
	handlePropertySelection,
} from "./GlobalPropertyMappingSelector.helpers";

export const UNASSIGNED_DROPPABLE_ID = "unassigned";

export function useGlobalPropertyMappingSelectorViewModel(
	props: GlobalPropertyMappingSelectorProps,
): GlobalPropertyMappingSelectorViewModel {
	const { onApplyMapping, availableProperties, autoGuessedMappings } = props;

	// Track which fields the user has explicitly modified
	const userSpecifiedFields = useRef<Set<keyof PropertyMappings>>(new Set());

	const [selectedValues, setSelectedValues] = useState<Partial<Record<keyof PropertyMappings, PropertyOption[]>>>({
		grower: [],
		farm: [],
		field: [],
		fieldId: [],
		labName: [],
		labInstructions: [],
		submissionNotes: [],
		yieldGoal: [],
		submitterName: [],
		submitterEmail: [],
		growerPhoneNumber: [],
		eventId: [],
		labSubmissionCode: [],
		externalId: [],
		externalFarmId: [],
		externalLabId: [],
		dateFieldReadyEstimated: [],
		density: [],
		depth: [],
		frequencyOfMicros: [],
		testPackage: [],
		fieldPriority: [],
		nextCrop: [],
		lastCrop: [],
		branch: [],
		salesperson: [],
		salespersonMobilePhone: [],
	});

	// Track if auto-guess notice should be shown
	const [showAutoGuessNotice, setShowAutoGuessNotice] = useState(false);

	// DnD multi-select state
	const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
	const lastClickedProperty = useRef<string | null>(null);
	const [draggingSelectedCount, setDraggingSelectedCount] = useState(0);
	const [sortOption, setSortOption] = useState<PropertySortOption>("name-asc");
	const [filterText, setFilterText] = useState("");

	// Initialize from auto-guessed values (only for fields user hasn't modified)
	useEffect(() => {
		if (!autoGuessedMappings) {
			return;
		}

		const fields = [
			"grower",
			"farm",
			"field",
			"fieldId",
			"labName",
			"labInstructions",
			"submissionNotes",
			"yieldGoal",
			"submitterName",
			"submitterEmail",
			"growerPhoneNumber",
			"eventId",
			"labSubmissionCode",
			"externalId",
			"externalFarmId",
			"externalLabId",
			"dateFieldReadyEstimated",
			"density",
			"depth",
			"frequencyOfMicros",
			"testPackage",
			"fieldPriority",
			"nextCrop",
			"lastCrop",
			"branch",
			"salesperson",
			"salespersonMobilePhone",
		] as const;
		const newSelectedValues = { ...selectedValues };
		let hasChanges = false;

		for (const field of fields) {
			if (!userSpecifiedFields.current.has(field)) {
				const autoGuessedValue = autoGuessedMappings[field];
				if (autoGuessedValue && autoGuessedValue.length > 0) {
					newSelectedValues[field] = autoGuessedValue;
					hasChanges = true;
				}
			}
		}

		if (hasChanges) {
			setSelectedValues(newSelectedValues);
			setShowAutoGuessNotice(true);
		}
		// NOTE: selectedValues intentionally omitted from deps to avoid infinite loop
	}, [autoGuessedMappings]);

	// Reset userSpecifiedFields when all files are cleared (autoGuessedMappings becomes null)
	useEffect(() => {
		if (autoGuessedMappings === null) {
			userSpecifiedFields.current.clear();
			setSelectedValues({
				grower: [],
				farm: [],
				field: [],
				fieldId: [],
				labName: [],
				labInstructions: [],
				submissionNotes: [],
				yieldGoal: [],
				submitterName: [],
				submitterEmail: [],
				growerPhoneNumber: [],
				eventId: [],
				labSubmissionCode: [],
				externalId: [],
				externalFarmId: [],
				externalLabId: [],
				dateFieldReadyEstimated: [],
				density: [],
				depth: [],
				frequencyOfMicros: [],
				testPackage: [],
				fieldPriority: [],
				nextCrop: [],
				lastCrop: [],
				branch: [],
				salesperson: [],
				salespersonMobilePhone: [],
			});
			setShowAutoGuessNotice(false);
			setSelectedProperties(new Set());
		}
	}, [autoGuessedMappings]);

	// Helper to update a field and notify parent
	const updateField = useCallback(
		(field: keyof PropertyMappings, properties: PropertyOption[]) => {
			setSelectedValues((prev) => ({
				...prev,
				[field]: properties,
			}));

			if (properties.length > 0) {
				onApplyMapping(
					field,
					properties.map((v) => v.name),
				);
			} else {
				onApplyMapping(field, null);
			}
		},
		[onApplyMapping],
	);

	// Filter fields based on company visibility settings
	const isFieldVisible = useCallback(
		(field: keyof PropertyMappings) => {
			if (field === "uniqueKey") return true;
			const column = field as ImportTableColumn;
			const settings = props.company?.portalSettings?.importTableColumns?.[column];
			return settings?.visible ?? DefaultImportTableColumns[column]?.visible ?? true;
		},
		[props.company],
	);

	// All company-visible fields
	const companyVisibleFields = useMemo(() => FIELD_CONFIGS.filter((f) => isFieldVisible(f.key)), [isFieldVisible]);

	// Filtered by show more/less
	const showMore = props.showMoreFields ?? false;
	const visibleFields = useMemo(() => {
		if (showMore) return companyVisibleFields;
		const visibleMap = new Map(companyVisibleFields.map((f) => [f.key, f]));
		return PRIMARY_PROPERTY_FIELDS_ORDERED.map((key) => visibleMap.get(key)).filter(
			(f): f is NonNullable<typeof f> => f != null,
		);
	}, [showMore, companyVisibleFields]);

	const hiddenFieldsCount = companyVisibleFields.length - visibleFields.length;

	// Count hidden fields that have mappings
	const hiddenFieldsWithMappingsCount = useMemo(() => {
		const visibleFieldKeys = new Set(visibleFields.map((f) => f.key));
		return companyVisibleFields.filter((f) => {
			if (visibleFieldKeys.has(f.key)) return false;
			const assigned = selectedValues[f.key];
			return assigned && assigned.length > 0;
		}).length;
	}, [visibleFields, companyVisibleFields, selectedValues]);

	const toggleShowMore = useCallback(() => {
		props.onToggleShowMore?.();
	}, [props.onToggleShowMore]);

	// Unassigned properties (full list, used for counting and shift-click ranges)
	const unassignedProperties = useMemo(
		() => getUnassignedProperties(availableProperties, selectedValues, sortOption),
		[availableProperties, selectedValues, sortOption],
	);

	// Filtered unassigned properties (what the UI renders)
	const filteredUnassignedProperties = useMemo(
		() => filterProperties(unassignedProperties, filterText),
		[unassignedProperties, filterText],
	);

	// DnD: before capture — track multi-drag count
	const handleBeforeCapture = useCallback(
		(before: BeforeCapture) => {
			if (selectedProperties.has(before.draggableId)) {
				setDraggingSelectedCount(selectedProperties.size);
			} else {
				setDraggingSelectedCount(0);
			}
		},
		[selectedProperties],
	);

	// DnD: drag end handler
	const handleDragEnd = useCallback(
		(result: DropResult) => {
			const { draggableId, source, destination } = result;
			setDraggingSelectedCount(0);
			if (!destination) return;

			const sourceId = source.droppableId;
			const targetId = destination.droppableId;
			if (sourceId === targetId) return;

			const isMultiDrag = selectedProperties.has(draggableId) && selectedProperties.size > 0;
			const propertyNames = isMultiDrag ? [...selectedProperties] : [draggableId];
			const properties = propertyNames
				.map((name) => availableProperties.find((p) => p.name === name))
				.filter((p): p is PropertyOption => p != null);

			// Mark target field as user-specified
			if (targetId !== UNASSIGNED_DROPPABLE_ID) {
				userSpecifiedFields.current.add(targetId as keyof PropertyMappings);
			}
			setShowAutoGuessNotice(false);

			// Remove from source field
			if (sourceId !== UNASSIGNED_DROPPABLE_ID) {
				const sourceField = sourceId as keyof PropertyMappings;
				const remaining = (selectedValues[sourceField] || []).filter((p) => !propertyNames.includes(p.name));
				userSpecifiedFields.current.add(sourceField);
				updateField(sourceField, remaining);
			}

			// Add to target field
			if (targetId !== UNASSIGNED_DROPPABLE_ID) {
				const targetField = targetId as keyof PropertyMappings;
				const current = selectedValues[targetField] || [];
				const newProps = properties.filter((p) => !current.some((c) => c.name === p.name));
				updateField(targetField, [...current, ...newProps]);
			}

			if (isMultiDrag) {
				setSelectedProperties(new Set());
				lastClickedProperty.current = null;
			}
		},
		[selectedProperties, availableProperties, selectedValues, updateField],
	);

	// Multi-select click handler
	const handlePropertyClick = useCallback(
		(name: string, shiftKey: boolean) => {
			const { selection, lastClicked } = handlePropertySelection(
				selectedProperties,
				name,
				shiftKey,
				lastClickedProperty.current,
				unassignedProperties,
			);
			setSelectedProperties(selection);
			lastClickedProperty.current = lastClicked;
		},
		[selectedProperties, unassignedProperties],
	);

	const clearSelection = useCallback(() => {
		setSelectedProperties(new Set());
		lastClickedProperty.current = null;
	}, []);

	// Mobile: assign all selected properties to a field
	const assignSelectedToField = useCallback(
		(fieldKey: keyof PropertyMappings) => {
			if (selectedProperties.size === 0) return;
			userSpecifiedFields.current.add(fieldKey);
			setShowAutoGuessNotice(false);
			const properties = [...selectedProperties]
				.map((name) => availableProperties.find((p) => p.name === name))
				.filter((p): p is PropertyOption => p != null);
			const current = selectedValues[fieldKey] || [];
			const newProps = properties.filter((p) => !current.some((c) => c.name === p.name));
			updateField(fieldKey, [...current, ...newProps]);
			setSelectedProperties(new Set());
			lastClickedProperty.current = null;
		},
		[selectedProperties, availableProperties, selectedValues, updateField],
	);

	// Mobile: remove a single property from a field
	const unassignPropertyFromField = useCallback(
		(fieldKey: keyof PropertyMappings, propertyName: string) => {
			userSpecifiedFields.current.add(fieldKey);
			const current = selectedValues[fieldKey] || [];
			updateField(
				fieldKey,
				current.filter((p) => p.name !== propertyName),
			);
		},
		[selectedValues, updateField],
	);

	return {
		selectedValues,
		availableProperties,
		shouldRender: availableProperties.length > 0,
		showAutoGuessNotice,
		visibleFields,
		hiddenFieldsCount,
		hiddenFieldsWithMappingsCount,
		showMoreFields: showMore,
		toggleShowMore,
		unassignedProperties,
		filteredUnassignedProperties,

		// Filter
		filterText,
		setFilterText,

		// DnD
		selectedProperties,
		draggingSelectedCount,
		sortOption,
		setSortOption,
		handleBeforeCapture,
		handleDragEnd,
		handlePropertyClick,
		clearSelection,
		UNASSIGNED_DROPPABLE_ID,

		// Mobile
		assignSelectedToField,
		unassignPropertyFromField,
	};
}
